import { ChevronDownIcon, ChevronRightIcon } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

const PermissionMatrix = ({
  roles,
  modules,
  rolePermissions,
  handlePermissionChange,
  handleModulePermissionChange,
}) => {
  const PermissionDropdown = ({ value, onChange, id, options }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);
    const buttonRef = useRef(null);

    const [openUp, setOpenUp] = useState(false);
    const [dropdownHeight, setDropdownHeight] = useState(0);
    React.useLayoutEffect(() => {
      if (!isOpen) return;
      if (!dropdownRef.current || !buttonRef.current) return;

      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdownRect = dropdownRef.current.getBoundingClientRect();

      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;

      setDropdownHeight(dropdownRect.height + 10);
      setOpenUp(
        spaceBelow < dropdownRect.height && spaceAbove > dropdownRect.height
      );
    }, [isOpen]);

    const selectedOption =
      options.find((opt) => opt.value === value) || 
      (value === "Partial" 
        ? { value: "Partial", bg: "bg-blue-100", text: "text-blue-600" }
        : options[0]);

    useEffect(() => {
      const handleClickOutside = (event) => {
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(event.target) &&
          buttonRef.current &&
          !buttonRef.current.contains(event.target)
        ) {
          setIsOpen(false);
        }
      };
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    return (
      <div className="relative">
        <button
          ref={buttonRef}
          type="button"
          className={`w-[150px] px-3 py-2 rounded-md flex items-center justify-between !border  ${selectedOption.bg} ${selectedOption.text} focus:outline-none`}
          style={{
            borderColor:
              value === "Read Only"
                ? "#f97316"
                : value === "Full Access"
                ? "#22c55e"
                : value === "Partial"
                ? "#3b82f6"
                : "#aeafb1",
          }}
          onClick={() => setIsOpen(!isOpen)}
        >
          <span>{value}</span>
          <ChevronDownIcon className="h-4 w-4 mr-1 text-gray-600 cursor-pointer" />
        </button>

        {isOpen &&
          createPortal(
            <div
              ref={dropdownRef}
              className="absolute z-50 mt-1 w-[150px] bg-white flex flex-col border border-gray-200 rounded-md shadow-lg"
              style={{
                top: openUp
                  ? buttonRef.current.getBoundingClientRect().top +
                    window.scrollY -
                    dropdownHeight
                  : buttonRef.current.getBoundingClientRect().bottom +
                    window.scrollY,
                left:
                  buttonRef.current.getBoundingClientRect().left +
                  window.scrollX,
              }}
            >
              {options.map((option) => (
                <button
                  key={option.value}
                  className="w-full px-3 py-2 text-left hover:bg-gray-100"
                  onClick={() => {
                    onChange(option.value);
                    setIsOpen(false);
                  }}
                >
                  {option.value}
                </button>
              ))}
            </div>,
            document.body
          )}
      </div>
    );
  };
  const [expandedModules, setExpandedModules] = useState({});
  // const [expandedFeatures, setExpandedFeatures] = useState({});
  const toggleModule = (module) => {
    setExpandedModules((prev) => ({
      ...prev,
      [module]: !prev[module],
    }));
  };

  const getPermissionsOfRole = (roleId) => {
    const role = rolePermissions.find((i) => i?.id === roleId);
    return role ? role.permissions : [];
  };

  // Recursive function to find permission by name in nested structure
  const findPermissionInTree = (permission, targetName) => {
    if (permission.name === targetName) {
      return permission;
    }
    if (Array.isArray(permission?.sub_permissions)) {
      for (const subPerm of permission.sub_permissions) {
        const found = findPermissionInTree(subPerm, targetName);
        if (found) return found;
      }
    }
    return null;
  };

  const findPermissionByName = (permissionName) => {
    // Recursively search all permissions from modules
    for (const module of modules) {
      for (const feature of module.features || []) {
        for (const perm of feature.permissions || []) {
          const found = findPermissionInTree(perm, permissionName);
          if (found) return found;
        }
      }
    }
    return null;
  };

  // Helper function to check if "view" is the first word of a permission name
  const isViewPermission = (permissionName) => {
    if (!permissionName) return false;
    const name = permissionName.toLowerCase().trim();
    // Split by underscore or dot and check if first word is "view"
    const firstWord = name.split(/[._]/)[0];
    return firstWord === "view";
  };

  const getModuleAccessLevel = (roleId, module) => {
    const assignedPermissionIds = getPermissionsOfRole(roleId);
    
    // Collect all permissions from all features in the module
    const allPermissions = module.features?.flatMap((f) => f.permissions || []) || [];
    
    if (allPermissions.length === 0) {
      return "No Access";
    }

    // Check for wildcard permission
    const wildcardPermission = findPermissionByName(`${module.key}.*`);
    if (wildcardPermission && assignedPermissionIds.includes(wildcardPermission.id)) {
      return "Full Access";
    }

    // Collect all permission IDs from all features
    const allPermissionIds = [];
    const viewPermissionIds = [];
    const actionPermissionIds = [];

    allPermissions.forEach((perm) => {
      allPermissionIds.push(...collectAllPermissionIds(perm));
      viewPermissionIds.push(...collectViewPermissionIds(perm));
      actionPermissionIds.push(...collectActionPermissionIds(perm));
    });

    const assignedViewIds = viewPermissionIds.filter((id) =>
      assignedPermissionIds.includes(id)
    );
    const assignedActionIds = actionPermissionIds.filter((id) =>
      assignedPermissionIds.includes(id)
    );

    const allAssigned =
      allPermissionIds.length > 0 &&
      allPermissionIds.every((id) => assignedPermissionIds.includes(id));
    const allViewAssigned =
      viewPermissionIds.length > 0 &&
      viewPermissionIds.every((id) => assignedPermissionIds.includes(id));
    const anyActionAssigned = assignedActionIds.length > 0;

   

    if (allViewAssigned && !anyActionAssigned) {
      return "Read Only";
    }
    if (allAssigned) {
      return "Full Access";
    }
    const anyAssigned = allPermissionIds.some((id) =>
      assignedPermissionIds.includes(id)
    );

    if (!anyAssigned) {
      return "No Access";
    }

    return "Partial";
  };

  const getAccessLevel = (roleId, feature) => {
    const assignedPermissionIds = getPermissionsOfRole(roleId);

    if (!feature?.permissions?.length) {
      return "No Access";
    }

    if (feature.permissions.length === 1) {
      const permission = feature.permissions[0];
      const permissionId = permission.id;
      const permissionName = permission.name;
      const isAssigned = assignedPermissionIds.includes(permissionId);

      if (!isAssigned) {
        if (hasSubPermissions(permission)) {
          const allNestedIds = collectAllPermissionIds(permission).filter(
            (id) => id !== permissionId
          );
          const anyNestedAssigned = allNestedIds.some((id) =>
            assignedPermissionIds.includes(id)
          );

          if (!anyNestedAssigned) {
            return "No Access";
          }

          const nestedViewIds = collectViewPermissionIds(permission).filter(
            (id) => id !== permissionId
          );
          const nestedActionIds = collectActionPermissionIds(permission).filter(
            (id) => id !== permissionId
          );

          const assignedNestedViewIds = nestedViewIds.filter((id) =>
            assignedPermissionIds.includes(id)
          );
          const assignedNestedActionIds = nestedActionIds.filter((id) =>
            assignedPermissionIds.includes(id)
          );

          const allNestedAssigned = allNestedIds.every((id) =>
            assignedPermissionIds.includes(id)
          );
          const allNestedViewAssigned =
            nestedViewIds.length > 0 &&
            nestedViewIds.every((id) => assignedPermissionIds.includes(id));

          // If parent is a view permission, it can only be "Read Only", not "Full Access"
          const parentIsView = isViewPermission(permissionName);
          if (allNestedAssigned) {
            // If parent is a view permission, it should always be "Read Only"
            // even if all nested permissions are assigned
            return parentIsView ? "Read Only" : "Full Access";
          }

          if (allNestedViewAssigned && assignedNestedActionIds.length === 0) {
            return "Read Only";
          }

          if (
            assignedNestedViewIds.length > 0 ||
            assignedNestedActionIds.length > 0
          ) {
            return "Partial";
          }
        }

        return "No Access";
      }

      const isView = isViewPermission(permissionName);

      // Check nested permissions for both wildcard and non-wildcard permissions with sub_permissions
      if (hasSubPermissions(permission)) {
        const allNestedIds = collectAllPermissionIds(permission).filter(
          (id) => id !== permissionId
        );
        const allNestedAssigned =
          allNestedIds.length > 0 &&
          allNestedIds.every((id) => assignedPermissionIds.includes(id));

        // If parent is a view permission, it can only be "Read Only", not "Full Access"
        if (allNestedAssigned) {
          return isView ? "Read Only" : "Full Access";
        }

        const nestedViewIds = collectViewPermissionIds(permission).filter(
          (id) => id !== permissionId
        );
        const nestedActionIds = collectActionPermissionIds(permission).filter(
          (id) => id !== permissionId
        );
        const assignedNestedViewIds = nestedViewIds.filter((id) =>
          assignedPermissionIds.includes(id)
        );
        const assignedNestedActionIds = nestedActionIds.filter((id) =>
          assignedPermissionIds.includes(id)
        );

        const allNestedViewAssigned =
          nestedViewIds.length > 0 &&
          nestedViewIds.every((id) => assignedPermissionIds.includes(id));

        if (allNestedViewAssigned && assignedNestedActionIds.length === 0) {
          return "Read Only";
        }

        if (
          assignedNestedViewIds.length > 0 ||
          assignedNestedActionIds.length > 0
        ) {
          return "Partial";
        }

        // If there are nested permissions but none are assigned, return "No Access"
        if (allNestedIds.length > 0) {
          return "No Access";
        }
      }

      return isView ? "Read Only" : "Full Access";
    }

    const permissionIds = feature.permissions.map((p) => p.id);
    const permissionNames = feature.permissions.map((p) => p.name);

    if (permissionIds.length > 1) {
      const moduleKey = feature.key || permissionNames[0]?.split(".")[0];
      const wildcardName = `${moduleKey}.*`;
      const wildcardPermission = findPermissionByName(wildcardName);

      if (
        wildcardPermission &&
        assignedPermissionIds.includes(wildcardPermission.id)
      ) {
        return "Full Access";
      }
    }

    const allPermissionIds = [];
    const allPermissions = [];

    feature.permissions.forEach((perm) => {
      allPermissionIds.push(...collectAllPermissionIds(perm));
      allPermissions.push(...collectAllPermissions(perm));
    });

    const viewPermissionIds = [];
    const actionPermissionIds = [];

    feature.permissions.forEach((perm) => {
      viewPermissionIds.push(...collectViewPermissionIds(perm));
      actionPermissionIds.push(...collectActionPermissionIds(perm));
    });

    const assignedViewIds = viewPermissionIds.filter((id) =>
      assignedPermissionIds.includes(id)
    );
    const assignedActionIds = actionPermissionIds.filter((id) =>
      assignedPermissionIds.includes(id)
    );

    const allAssigned =
      allPermissionIds.length > 0 &&
      allPermissionIds.every((id) => assignedPermissionIds.includes(id));
    const allViewAssigned =
      viewPermissionIds.length > 0 &&
      viewPermissionIds.every((id) => assignedPermissionIds.includes(id));
    const anyActionAssigned = assignedActionIds.length > 0;

    if (allAssigned) {
      return "Full Access";
    }

    if (allViewAssigned && !anyActionAssigned) {
      return "Read Only";
    }

    const anyAssigned = allPermissionIds.some((id) =>
      assignedPermissionIds.includes(id)
    );

    if (!anyAssigned) {
      return "No Access";
    }

    return "Partial";
  };
  const formatPermissionLabel = (perm) => {
    const permName = typeof perm === "string" ? perm : perm.name;
    const cleanName = permName
      .replace(/\..*$/, "") 
      .replace(/_cv|_dm|_dispatch/gi, "")
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (c) => c.toUpperCase());

    if (permName.endsWith(".*")) {
      return `${cleanName} (All Permissions)`;
    }

    return cleanName;
  };

  const getModuleOptions = (module) => {
    const permissions =
      module.features?.flatMap((f) => f.permissions || []) || [];

    const allPermissionNames = [];
    permissions.forEach((perm) => {
      const allPerms = collectAllPermissions(perm);
      allPermissionNames.push(
        ...allPerms.map((p) => p.name?.toLowerCase() || "")
      );
    });

    const topLevelNames = permissions.map((p) =>
      typeof p === "string" ? p.toLowerCase() : p.name?.toLowerCase()
    );

    const allNames = [...topLevelNames, ...allPermissionNames];

    const hasView = allNames.some((n) => n && isViewPermission(n));
    const hasNonView = allNames.some((n) => n && !isViewPermission(n));

    if (hasView && !hasNonView) {
      return [
        { value: "Read Only", bg: "bg-orange-100", text: "text-orange-600" },
        { value: "No Access", bg: "bg-gray-100", text: "text-gray-800" },
      ];
    }

    if (!hasView && hasNonView) {
      return [
        { value: "Full Access", bg: "bg-green-100", text: "text-green-800" },
        { value: "No Access", bg: "bg-gray-100", text: "text-gray-800" },
      ];
    }

    return [
      { value: "Full Access", bg: "bg-green-100", text: "text-green-800" },
      { value: "Read Only", bg: "bg-orange-100", text: "text-orange-600" },
      { value: "No Access", bg: "bg-gray-100", text: "text-gray-800" },
    ];
  };

  const getOptions = (perm) => {
    const permName = typeof perm === "string" ? perm : perm.name;
    const p = permName.toLowerCase();

    const hasNested = hasSubPermissions(perm);

    if (hasNested) {
      const allNestedPerms = collectAllPermissions(perm);
      const nestedNames = allNestedPerms.map(
        (np) => np.name?.toLowerCase() || ""
      );

      const hasView = nestedNames.some((n) => n && isViewPermission(n));
      const hasNonView = nestedNames.some((n) => n && !isViewPermission(n));

      const parentIsView = isViewPermission(p);

      if ((hasView && hasNonView) || (!parentIsView && hasView)) {
        return [
          { value: "Full Access", bg: "bg-green-100", text: "text-green-800" },
          { value: "Read Only", bg: "bg-orange-100", text: "text-orange-600" },
          { value: "No Access", bg: "bg-gray-100", text: "text-gray-800" },
        ];
      }

      if (hasView && !hasNonView) {
        return [
          { value: "Read Only", bg: "bg-orange-100", text: "text-orange-600" },
          { value: "No Access", bg: "bg-gray-100", text: "text-gray-800" },
        ];
      }

      if (!hasView && hasNonView) {
        return [
          { value: "Full Access", bg: "bg-green-100", text: "text-green-800" },
          { value: "No Access", bg: "bg-gray-100", text: "text-gray-800" },
        ];
      }
    }

    if (isViewPermission(p) && p.includes("*")) {
      return [
        { value: "Read Only", bg: "bg-orange-100", text: "text-orange-600" },
        { value: "No Access", bg: "bg-gray-100", text: "text-gray-800" },
      ];
    }

    if (isViewPermission(p)) {
      return [
        { value: "Read Only", bg: "bg-orange-100", text: "text-orange-600" },
        { value: "No Access", bg: "bg-gray-100", text: "text-gray-800" },
      ];
    }

    return [
      { value: "Full Access", bg: "bg-green-100", text: "text-green-800" },
      { value: "No Access", bg: "bg-gray-100", text: "text-gray-800" },
      { value: "Read Only", bg: "bg-orange-100", text: "text-orange-600" },
    ];
  };

  const hasSubPermissions = (perm) => {
    if (
      !Array.isArray(perm?.sub_permissions) ||
      perm.sub_permissions.length === 0
    ) {
      return false;
    }
    return perm.sub_permissions.length > 0;
  };

  const collectAllPermissionIds = (perm) => {
    const ids = [perm.id];
    if (Array.isArray(perm?.sub_permissions)) {
      perm.sub_permissions.forEach((subPerm) => {
        ids.push(...collectAllPermissionIds(subPerm));
      });
    }
    return ids;
  };

  const collectAllPermissions = (perm) => {
    const perms = [perm];
    if (Array.isArray(perm?.sub_permissions)) {
      perm.sub_permissions.forEach((subPerm) => {
        perms.push(...collectAllPermissions(subPerm));
      });
    }
    return perms;
  };

  const collectViewPermissionIds = (perm) => {
    const ids = [];
    if (isViewPermission(perm.name)) {
      ids.push(perm.id);
    }
    if (Array.isArray(perm?.sub_permissions)) {
      perm.sub_permissions.forEach((subPerm) => {
        ids.push(...collectViewPermissionIds(subPerm));
      });
    }
    return ids;
  };

  const collectActionPermissionIds = (perm) => {
    const ids = [];
    if (perm.name && !isViewPermission(perm.name)) {
      ids.push(perm.id);
    }
    if (Array.isArray(perm?.sub_permissions)) {
      perm.sub_permissions.forEach((subPerm) => {
        ids.push(...collectActionPermissionIds(subPerm));
      });
    }
    return ids;
  };

  const [expandedPermissions, setExpandedPermissions] = useState({});

  const togglePermissionExpand = (permId, parentPath = "") => {
    const parentPathStr = String(parentPath || "");
    const path = parentPathStr ? `${parentPathStr}.${permId}` : String(permId);
    setExpandedPermissions((prev) => ({
      ...prev,
      [path]: !prev[path],
    }));
  };

  const isPermissionExpanded = (permId, parentPath = "") => {
    const parentPathStr = String(parentPath || "");
    const path = parentPathStr ? `${parentPathStr}.${permId}` : String(permId);
    return expandedPermissions[path] || false;
  };

  const renderPermissionRow = (perm, paddingLeft = 16, parentPath = "") => {
    const parentPathStr = String(parentPath || "");
    const currentPath = parentPathStr
      ? `${parentPathStr}.${perm.id}`
      : String(perm.id);
    const isExpanded = isPermissionExpanded(perm.id, parentPathStr);
    const hasSubs = hasSubPermissions(perm);

    const depth = parentPathStr ? parentPathStr.split(".").length : 0;

    const bgColor = depth % 2 === 0 ? "bg-gray-50" : "bg-white";

    const calculatedPadding = paddingLeft + depth * 40;

    return (
      <React.Fragment key={perm.id}>
        <tr className={`border-b ${bgColor}`}>
          <td
            className={`
                            sticky left-0 z-30 ${bgColor}
                            pr-3 whitespace-nowrap 
                            border-r border-gray-200
                        `}
            style={{ paddingLeft: `${calculatedPadding}px` }}
          >
            <span className="flex items-center font-normal">
              {hasSubs &&
                (isExpanded ? (
                  <ChevronDownIcon
                    className="h-4 w-4 mr-1 text-gray-600 cursor-pointer"
                    onClick={() => togglePermissionExpand(perm.id, parentPath)}
                  />
                ) : (
                  <ChevronRightIcon
                    className="h-4 w-4 mr-1 text-gray-600 cursor-pointer"
                    onClick={() => togglePermissionExpand(perm.id, parentPath)}
                  />
                ))}

              <span
                className="cursor-pointer select-none"
                onClick={() =>
                  hasSubs && togglePermissionExpand(perm.id, parentPath)
                }
              >
                {perm.label}
              </span>
            </span>
          </td>

          {roles.map((role) => (
            <td key={role.id} className="px-6 py-3 border-r">
              <PermissionDropdown
                options={getOptions(perm)}
                value={getAccessLevel(role.id, {
                  permissions: [perm], // Pass full permission object including sub_permissions
                })}
                onChange={(v) => handlePermissionChange(role.id, perm, v)}
              />
            </td>
          ))}
        </tr>

        {/* Recursively render sub_permissions */}
        {hasSubs &&
          isExpanded &&
          Array.isArray(perm.sub_permissions) &&
          perm.sub_permissions.map((subPerm) =>
            renderPermissionRow(subPerm, calculatedPadding, currentPath)
          )}
      </React.Fragment>
    );
  };

  return (
    <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-auto h-[calc(100vh-290px)]">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="sticky z-50">
          <tr>
            <th
              className="
							sticky left-0 top-0 z-20 bg-blue-600
							px-4 py-3 text-left text-white
							relative
							after:content-['']
							after:absolute
							after:top-0
							after:right-0
							after:h-full
							after:w-[1px]
							after:bg-gray-500
                            text-base font-bold
						"
            >
              Module / Feature
            </th>
            {roles.map((role, index) => (
              <th
                key={role?.id}
                className="
							bg-blue-600
							px-4 py-3 text-left text-white whitespace-nowrap
							relative
							after:content-['']
							after:absolute
							after:top-0
							after:right-0
							after:h-full
							after:w-[1px]
							after:bg-gray-500
                            text-base font-bold
						"
              >
                {role?.name}
              </th>
            ))}
          </tr>
        </thead>

        <tbody className="bg-white">
          {modules.map((moduleItem, moduleIndex) => {
            const isModuleOpen = expandedModules[moduleItem.key];

            return (
              <React.Fragment key={moduleItem.key}>
                <tr className="border-b border-gray-200">
                  <td
                    className="
										sticky left-0 z-30 bg-white
										 px-3 py-3 whitespace-nowrap cursor-pointer
										border-r border-gray-200
										shadow-[inset_0_-1px_0_#e5e7eb]
										"
                    onClick={() => toggleModule(moduleItem.key)}
                  >
                    <span className="flex items-center font-medium">
                      {isModuleOpen ? (
                        <ChevronDownIcon className="h-4 w-4 mr-1 text-gray-600 cursor-pointer" />
                      ) : (
                        <ChevronRightIcon className="h-4 w-4 mr-1 text-gray-600 cursor-pointer" />
                      )}

                      {moduleItem.label}
                    </span>
                  </td>

                  {roles.map((role) => (
                    <td key={role.id} className="px-6 py-3 border-r">
                      <PermissionDropdown
                        value={getModuleAccessLevel(role.id, moduleItem)}
                        options={getModuleOptions(moduleItem)}
                        onChange={(value) =>
                          handleModulePermissionChange(
                            role.id,
                            moduleItem,
                            value
                          )
                        }
                      />
                    </td>
                  ))}
                </tr>
                {isModuleOpen &&
                  moduleItem.features?.map((feature, featureIndex) => {
                    // Filter out the wildcard permission that matches the module key
                    // e.g., if module is "data_management", filter out "data_management.*"
                    const filteredPermissions =
                      feature.permissions?.filter((perm) => {
                        // If permission name ends with .*, check if it matches module key
                        if (perm.name.endsWith(".*")) {
                          const permKey = perm.name.replace(".*", "");
                          // Don't show if it matches the module key
                          return permKey !== moduleItem.key;
                        }
                        return true;
                      }) || [];

                    return (
                      <React.Fragment key={feature.key}>
                        {filteredPermissions.map((perm) => (
                          <React.Fragment key={perm.id}>
                            {renderPermissionRow(perm, 30, "")}
                          </React.Fragment>
                        ))}
                      </React.Fragment>
                    );
                  })}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default PermissionMatrix;
